// @dynamic-i18n-keys
// sidebar.blockTitle.*
import React, { useContext } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import {
  BiData,
  MoreLikeThisClickBiEvent,
  BiContextProvidedValue,
} from '@wix/da-bi/pkg/events';
import { PapiDeviation, RelatedBlock } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import MltSearch from '@wix/da-ds/pkg/Icons/24x24/MltSearch';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import { useCurrentUser } from '../../../utils/hooks/useCurrentUser';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import {
  getDeviationResourceType,
  getIsPost,
} from '../../../DeviationViews/_helpers';
import { DeviationResourceType } from '../../../types/deviation';
import { AuthorWatchButton } from '../../../Button';
import { getBlockBI } from '../../helpers';
import SeeAllLink from '../SeeAllLink';
import MoreByAuthor from '../MoreByAuthor';
import ContentByUser from '../ContentByUser';
import OwnedByUser from '../OwnedByUser';

import s from './SidebarBlockWrapper.scss';

export interface Props {
  deviation: PapiDeviation;
  block: RelatedBlock;
  /** do not show a title or show-all/watch/mlt button above the block */
  noHeader?: boolean;
  className?: string;
  headerClassName?: string;
  titleClassName?: string;
  seeAllClassName?: string;
  onSeeAllClick?: () => void;
}

const SidebarBlockWrapper: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  block: {
    contentTitleType,
    contentType,
    seeAllType = 'seeall',
    seeAllUrl,
    titleType = 'plaintext',
    linkUrl,
    deviations: blockDeviations,
  },
  noHeader,
  children,
  className,
  headerClassName,
  titleClassName,
  seeAllClassName,
  onSeeAllClick,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const showSeeAllLink = !!seeAllUrl || !!onSeeAllClick;

  const { author } = deviation;
  const resourceType = getDeviationResourceType(deviation);
  const { needsSubscription, isSubscribedButBlocked } =
    useSubscriptionTier(deviation);
  const currentUser = useCurrentUser();
  const isDisplayingAnotherUsersMoreBy =
    !showSeeAllLink &&
    titleType === 'more-by' &&
    author &&
    author?.userId !== currentUser.userId;
  const seeAllAlwaysVisible =
    showSeeAllLink && titleType === 'plaintext' && seeAllUrl;

  // precondition: the owner is the same for all deviations in the block
  const [firstBlockDeviation] = blockDeviations || [];
  const owner = firstBlockDeviation?.adoptable?.owner;
  const isNotOwner = owner?.userId !== currentUser.userId;
  const emphasisOnOwner = contentType === 'owned_by' && owner;

  const isMlt =
    contentType === 'recommended' &&
    resourceType === DeviationResourceType.ART &&
    !getIsPost(deviation) &&
    !(needsSubscription || isSubscribedButBlocked);

  const header = noHeader ? null : (
    <div className={classnames(s['block-header'], headerClassName)}>
      <span className={classnames(s['block-title'], titleClassName)}>
        {emphasisOnOwner ? (
          <OwnedByUser owner={owner} />
        ) : titleType === 'more-by' && deviation.author ? (
          <MoreByAuthor deviation={deviation} contentType={contentType} />
        ) : titleType === 'link' && linkUrl ? (
          <ContentByUser
            contentTitle={t(`sidebar.blockTitle.${contentType}`, {
              author: deviation.author?.username,
            })}
            contentUrl={linkUrl}
          />
        ) : isMlt ? (
          t(`sidebar.blockTitle.${contentType}.mlt`)
        ) : (
          t(`sidebar.blockTitle.${contentType}`, {
            author: deviation.author?.username,
          })
        )}
      </span>

      {showSeeAllLink && (
        <SeeAllLink
          contentType={contentType}
          deviation={deviation}
          type={seeAllType}
          url={seeAllUrl}
          onClick={onSeeAllClick}
          className={classnames(s['see-all'], seeAllClassName)}
        />
      )}

      {!isMobile && isDisplayingAnotherUsersMoreBy && (
        <AuthorWatchButton className={s['watch-button']} author={author} />
      )}
      {!isMobile && emphasisOnOwner && isNotOwner && (
        <AuthorWatchButton className={s['watch-button']} author={owner} />
      )}
      {isMlt && (
        <IconButton
          className={s['mlt-button']}
          size="medium"
          icon={MltSearch}
          aria-label={t('common.moreLikeThis')}
          href={Url.moreLikeThisLink(deviation.deviationId)}
          biData={BiData<MoreLikeThisClickBiEvent>({
            evid: 126,
            typeid: resourceType,
            itemid: deviation.deviationId,
            creatorid: deviation.author?.useridUuid ?? '',
            sectionname: BiContextProvidedValue,
          })}
        />
      )}
    </div>
  );

  return (
    <BiLoggerContextProvider value={getBlockBI(contentType)}>
      <div
        className={classnames(
          s['root'],
          seeAllAlwaysVisible && s['see-all-visible'],
          className
        )}
      >
        {header}
        {children}
      </div>
    </BiLoggerContextProvider>
  );
};
SidebarBlockWrapper.displayName = 'SidebarBlockWrapper';

export default SidebarBlockWrapper;
